"use client";

import {
   useMemo,
   useRef,
   useState,
   ReactNode,
   createContext,
   useCallback,
   useContext,
   useEffect
} from "react";
import { safeLocalStorage } from "@/app/lib/storage";
import { METHOD_TYPE, STORAGE_KEYS } from "@/app/lib/enums";
import {
   getAccessToken,
   removeAccessToken,
   removeSharedAuthToken,
   safeCookie,
   SHARED_AUTH_TOKEN_KEY,
   setAccessToken
} from "@/app/lib/cookie";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import type { UserProfile } from "@/types";
import { useAuthSyncStore } from "@/app/store/auth-sync";

interface AuthContextType {
   isLoggedIn: boolean;
   checkAuth: (options?: { forceRefresh?: boolean }) => Promise<boolean>;
   user: UserProfile | null;
   logout: () => void;
   isInitializing: boolean;
   isSharedLoginAuthenticating: boolean;
}

const AuthContext = createContext<AuthContextType>({
   isLoggedIn: false,
   checkAuth: async () => false,
   user: null,
   logout: () => {},
   isInitializing: true,
   isSharedLoginAuthenticating: false
});

const AUTH_PROFILE_REFRESH_INTERVAL_MS = 60 * 60 * 1000;

export function useAuth() {
   return useContext(AuthContext);
}

const clearAuthData = () => {
   safeLocalStorage.clear();
   removeSharedAuthToken();
   removeAccessToken();
};

type AuthenticatedUserPayload = Partial<UserProfile> &
   Pick<UserProfile, "id" | "email" | "username" | "status" | "createdAt">;

const buildAuthenticatedUser = (payload: AuthenticatedUserPayload): UserProfile => ({
   ...payload,
   id: payload.id,
   email: payload.email,
   username: payload.username,
   status: payload.status,
   createdAt: payload.createdAt,
   avatarUrl: payload.avatarUrl,
   isAdmin: payload.isAdmin || false,
   role: payload.role,
   projects: payload.projects,
   metaData: payload.metaData
});

export function AuthProvider({ children }: { children: ReactNode }) {
   const [isLoggedIn, setIsLoggedIn] = useState(() => !!getAccessToken());
   const [user, setUser] = useState<UserProfile | null>(null);
   const [isInitializing, setIsInitializing] = useState(true);
   const [isSharedLoginAuthenticating, setIsSharedLoginAuthenticating] = useState(false);
   const isCheckingAuth = useRef(false);
   const mountedRef = useRef(false);
   const authActionInProgress = useRef(false);
   const retryCount = useRef(0);
   const initialAuthChecked = useRef(false);
   const MAX_RETRIES = 3;
   const {
      setExternalProfileSyncedAt,
      markForcedAuthRefresh,
      shouldForceRefreshAuthProfile,
      clearAuthSync
   } = useAuthSyncStore();

   const handleAuthAction = useCallback(async (action: () => Promise<void>) => {
      if (authActionInProgress.current) {
         return;
      }

      try {
         authActionInProgress.current = true;
         await action();
      } finally {
         authActionInProgress.current = false;
      }
   }, []);

   const getLocale = useCallback(() => {
      const pathSegments = window.location.pathname.split("/");
      return pathSegments[1] || "vi";
   }, []);

   const handleLogout = useCallback(async () => {
      if (!mountedRef.current) return;

      await handleAuthAction(async () => {
         clearAuthData();
         clearAuthSync();
         setUser(null);
         setIsLoggedIn(false);
         const locale = getLocale();
         window.location.href = `/${locale}/auth/login`;
      });
   }, [clearAuthSync, getLocale]);

   const checkAuth = useCallback(
      async (options?: { forceRefresh?: boolean }) => {
         if (isCheckingAuth.current || !mountedRef.current) {
            return false;
         }

         if (typeof window === "undefined") {
            return false;
         }

         isCheckingAuth.current = true;

         try {
            const refreshParam = options?.forceRefresh ? "?forceRefresh=true" : "";
            const verifyResponse = await fetchWithAuth(
               `${api_url.verify_token_url}${refreshParam}`
            );
            const verifyData = await verifyResponse.json();
            if (!verifyResponse.ok || !verifyData.valid) {
               console.warn("[Auth] Token verification failed:", verifyData.message);
               if (verifyResponse.status >= 500 && retryCount.current < MAX_RETRIES) {
                  retryCount.current++;

                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  return checkAuth(options);
               }

               if (
                  verifyResponse.status === 401 ||
                  verifyData.message.toLowerCase().includes("token")
               ) {
                  await handleLogout();
               }
               return false;
            }

            retryCount.current = 0;
            const authenticatedUser = buildAuthenticatedUser(verifyData.payload);
            setUser(authenticatedUser);
            setIsLoggedIn(true);
            safeLocalStorage.setItem(
               STORAGE_KEYS.USER,
               JSON.stringify(authenticatedUser)
            );
            const externalProfileSyncedAt =
               authenticatedUser.metaData?.externalProfileSyncedAt;
            if (options?.forceRefresh) {
               markForcedAuthRefresh(externalProfileSyncedAt);
            } else {
               setExternalProfileSyncedAt(externalProfileSyncedAt);
            }

            return true;
         } catch (error) {
            console.error("[Auth] Error during auth check:", error);

            if (retryCount.current < MAX_RETRIES) {
               retryCount.current++;

               await new Promise((resolve) => setTimeout(resolve, 1000));
               return checkAuth(options);
            }

            await handleLogout();
            return false;
         } finally {
            isCheckingAuth.current = false;
         }
      },
      [handleLogout, markForcedAuthRefresh, setExternalProfileSyncedAt]
   );

   useEffect(() => {
      if (!mountedRef.current) {
         mountedRef.current = true;
      }

      const initAuth = async () => {
         try {
            let token = getAccessToken();
            if (!token && safeCookie.get(SHARED_AUTH_TOKEN_KEY)) {
               setIsSharedLoginAuthenticating(true);
               const sharedLoginResponse = await fetch(api_url.shared_login_url, {
                  method: METHOD_TYPE.POST,
                  headers: {
                     "Content-Type": "application/json"
                  }
               });
               if (sharedLoginResponse.ok) {
                  const sharedLoginData = await sharedLoginResponse.json();
                  if (sharedLoginData.authenticated && sharedLoginData.token) {
                     setAccessToken(sharedLoginData.token);
                     token = sharedLoginData.token;
                     if (sharedLoginData.user) {
                        const authenticatedUser = buildAuthenticatedUser(
                           sharedLoginData.user
                        );
                        setUser(authenticatedUser);
                        setIsLoggedIn(true);
                        safeLocalStorage.setItem(
                           STORAGE_KEYS.USER,
                           JSON.stringify(authenticatedUser)
                        );
                     }
                     const locale = getLocale();
                     const currentPath = window.location.pathname;
                     window.location.href = currentPath.includes("/auth/login")
                        ? `/${locale}`
                        : currentPath;
                     return;
                  }
               }
               setIsSharedLoginAuthenticating(false);
            }

            if (!token) {
               if (isLoggedIn) {
                  await handleLogout();
               }
               setIsInitializing(false);
               return;
            }

            if (!initialAuthChecked.current) {
               const success = await checkAuth({
                  forceRefresh: shouldForceRefreshAuthProfile()
               });
               if (success) {
                  initialAuthChecked.current = true;
               }
            }
         } catch (error) {
            console.error("[Auth] Init error:", error);
            setIsSharedLoginAuthenticating(false);
            await handleLogout();
         } finally {
            setIsInitializing(false);
         }
      };

      if (typeof window !== "undefined") {
         initAuth();
      }

      return () => {
         mountedRef.current = false;
      };
   }, [checkAuth, handleLogout, isLoggedIn, shouldForceRefreshAuthProfile]);

   const handleNavigation = useCallback(() => {
      if (isInitializing || !mountedRef.current) return;
      const currentPath = window.location.pathname;

      if (currentPath.includes("/auth/")) {
         return;
      }

      if (!isLoggedIn && !currentPath.includes("/auth/")) {
         const locale = getLocale();
         const loginPath = `/${locale}/auth/login`;

         if (currentPath !== loginPath && !initialAuthChecked.current) {
            window.location.href = loginPath;
         }
      }
   }, [isInitializing, isLoggedIn, getLocale]);

   useEffect(() => {
      handleNavigation();
   }, [handleNavigation]);

   useEffect(() => {
      if (!isLoggedIn || isInitializing) return;

      const refreshExternalProfile = () => {
         if (document.visibilityState !== "visible") return;
         if (!shouldForceRefreshAuthProfile()) return;
         void checkAuth({ forceRefresh: true });
      };

      const intervalId = window.setInterval(
         refreshExternalProfile,
         AUTH_PROFILE_REFRESH_INTERVAL_MS
      );

      return () => {
         window.clearInterval(intervalId);
      };
   }, [checkAuth, isInitializing, isLoggedIn, shouldForceRefreshAuthProfile]);

   useEffect(() => {
      mountedRef.current = true;
      return () => {
         mountedRef.current = false;
      };
   }, []);

   const contextValue = useMemo(
      () => ({
         isLoggedIn,
         checkAuth,
         user,
         logout: handleLogout,
         isInitializing,
         isSharedLoginAuthenticating
      }),
      [
         isLoggedIn,
         checkAuth,
         user,
         handleLogout,
         isInitializing,
         isSharedLoginAuthenticating
      ]
   );

   return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
